import React, { useState, useCallback } from 'react';
import useFeaturesListStore from '../stores/featuresListStore';
import type { Feature } from '../stores/featuresListStore';
import './FeaturesTable.css';

interface FeaturesTableProps {
  subProjectId: string;
  containerClassName?: string;
  tableClassName?: string;
  emptyClassName?: string;
  showResultsColumn?: boolean;
  emptyMessage?: string;
}

const FeaturesTable: React.FC<FeaturesTableProps> = ({
  subProjectId: _subProjectId,
  containerClassName = 'features-table-container',
  tableClassName = 'features-table',
  emptyClassName = 'features-table-empty',
  showResultsColumn = true,
  emptyMessage = 'No features yet. Click "Extract Features" to generate from conversation.'
}) => {
  const features = useFeaturesListStore((state) => state.features);
  const updateFeature = useFeaturesListStore((state) => state.updateFeature);
  const toggleFeatureIncluded = useFeaturesListStore((state) => state.toggleFeatureIncluded);
  const toggleDescriptionExpanded = useFeaturesListStore((state) => state.toggleDescriptionExpanded);
  const toggleKeywordsExpanded = useFeaturesListStore((state) => state.toggleKeywordsExpanded);

  const [editingFeatureId, setEditingFeatureId] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<'name' | 'description' | 'keywords' | null>(null);
  const [editValue, setEditValue] = useState('');

  const handleStartEdit = useCallback((featureId: string, field: 'name' | 'description' | 'keywords', currentValue: string) => {
    setEditingFeatureId(featureId);
    setEditingField(field);
    setEditValue(currentValue);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (!editingFeatureId || !editingField) return;

    const updates: Partial<Feature> = {};
    if (editingField === 'name') {
      updates.feature_name = editValue;
    } else if (editingField === 'description') {
      updates.feature_description = editValue;
    } else if (editingField === 'keywords') {
      updates.keywords = editValue;
    }

    updateFeature(editingFeatureId, updates);
    setEditingFeatureId(null);
    setEditingField(null);
    setEditValue('');
  }, [editingFeatureId, editingField, editValue, updateFeature]);

  const handleCancelEdit = useCallback(() => {
    setEditingFeatureId(null);
    setEditingField(null);
    setEditValue('');
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  }, [handleSaveEdit, handleCancelEdit]);

  const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (features.length === 0) {
    return (
      <div className={emptyClassName}>
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <table className={tableClassName}>
        <thead>
          <tr>
            <th className="col-number">Nr</th>
            <th className="col-select">Select</th>
            <th className="col-features">Features</th>
            <th className="col-keywords">Keywords</th>
            {showResultsColumn && <th className="col-results">Nr Results</th>}
          </tr>
        </thead>
        <tbody>
          {features.map((feature) => (
            <tr key={feature.id} className={feature.included_in_search ? 'selected' : ''}>
              {/* Column 1: Number */}
              <td className="col-number">{feature.number}</td>

              {/* Column 2: Select */}
              <td className="col-select">
                <input
                  type="checkbox"
                  checked={feature.included_in_search}
                  onChange={() => toggleFeatureIncluded(feature.id)}
                  title={feature.included_in_search ? 'Exclude from search' : 'Include in search'}
                />
              </td>

              {/* Column 3: Features */}
              <td className="col-features">
                <div className="feature-content">
                  {/* Feature Name */}
                  <div className="feature-name">
                    {editingFeatureId === feature.id && editingField === 'name' ? (
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleSaveEdit}
                        onKeyDown={handleKeyDown}
                        autoFocus
                        className="edit-input"
                      />
                    ) : (
                      <span
                        onClick={() => handleStartEdit(feature.id, 'name', feature.feature_name)}
                        className="editable"
                        title="Click to edit"
                      >
                        {feature.feature_name}
                      </span>
                    )}
                    <button
                      className="expand-btn"
                      onClick={() => toggleDescriptionExpanded(feature.id)}
                      title={feature.description_expanded ? 'Collapse' : 'Expand'}
                    >
                      {feature.description_expanded ? '▼' : '▶'}
                    </button>
                  </div>

                  {/* Feature Description (expandable) */}
                  {feature.description_expanded && (
                    <div className="feature-description">
                      {editingFeatureId === feature.id && editingField === 'description' ? (
                        <textarea
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={handleSaveEdit}
                          onKeyDown={handleKeyDown}
                          autoFocus
                          className="edit-textarea"
                          rows={3}
                        />
                      ) : (
                        <span
                          onClick={() => handleStartEdit(feature.id, 'description', feature.feature_description)}
                          className="editable"
                          title="Click to edit"
                        >
                          {feature.feature_description}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </td>

              {/* Column 4: Keywords */}
              <td className="col-keywords">
                <div className="keywords-content">
                  {editingFeatureId === feature.id && editingField === 'keywords' ? (
                    <textarea
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={handleSaveEdit}
                      onKeyDown={handleKeyDown}
                      autoFocus
                      className="edit-textarea"
                      rows={2}
                    />
                  ) : (
                    <>
                      <span
                        onClick={() => handleStartEdit(feature.id, 'keywords', feature.keywords || '')}
                        className="editable"
                        title="Click to edit keywords"
                      >
                        {feature.keywords_expanded
                          ? feature.keywords || 'No keywords yet'
                          : truncateText(feature.keywords || 'No keywords yet', 40)
                        }
                      </span>
                      {feature.keywords && feature.keywords.length > 40 && (
                        <button
                          className="expand-btn"
                          onClick={() => toggleKeywordsExpanded(feature.id)}
                          title={feature.keywords_expanded ? 'Collapse' : 'Expand'}
                        >
                          {feature.keywords_expanded ? '▼' : '▶'}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </td>

              {/* Column 5: Nr Results */}
              {showResultsColumn && (
                <td className="col-results">
                  <div className="results-content">
                    <span className="result-count">--</span>
                    <span className="result-label">docs</span>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default FeaturesTable;
